import { LogLevel, LogService } from "@/services/logging";
import UserModule from "@/store/modules/user";
import Timeout from "await-timeout";
import { hasSalesStarted } from "./cash-float";
import { dexieDB } from "@/services/dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";

// Indicates whether a db reset is running. ensures that we don't run multiple resets at the same time
let resetRunning = false;

/**
 * check if we are allowed to reset the database
 * Resetting the database means deleting all the data and starting from scratch.
 *
 * This is only allowed if sales are closed
 */
export function canResetDatabase() {
  if (UserModule.isColleague) {
    return true;
  }
  return !hasSalesStarted.value;
}

/**
 * Reset the database growing collections (Dexie tables)
 */
export async function resetGrowingCollections() {
  await dexieDB.orders.clear();
  await dexieDB.logs.clear();
  await dexieDB.cashTransfers.clear();

  LogService.info("Cleared orders, logs and cashTransfers tables");
}

/**
 * Reset DB helper method to be used in components.
 * If the reset is already running, it will not run another one.
 * If the reset is not allowed, it will retry 5 times.
 */
export async function resetGrowingCollecitonsFromUI() {
  // If a reset is already running, don't run another one
  if (resetRunning) {
    return;
  }

  resetRunning = true;

  // We will retry for 5 times, waiting retryTimeout ms between each retry
  const retryTimeout = 5000;
  let retries = 0;

  while (retries++ < 5) {
    // Reset the db if possible and break the loop
    if (canResetDatabase()) {
      try {
        await resetGrowingCollections();
        LogService.info("DB reset successfully");
        break;
      } catch (error) {
        LogService.info("Error resetting DB", { error }, LogLevel.Error);
      }
    }
    if (retries < 5) {
      LogService.info(`Cannot reset DB, will retry in ${retryTimeout} ms`);
    } else {
      LogService.info("Could not reset DB, stopping");
    }
    await Timeout.set(retryTimeout);
  }

  // This is no longer running;
  resetRunning = false;
}

/**
 * Log pending, ignored, and failed orders in Dexie DB
 * @param afterClosingSales If true, appends "after closing sales" to log messages
 */
export async function logPendingIgnoredAndFailedOrders(
  afterClosingSales?: boolean
) {
  const context = afterClosingSales ? "after closing sales" : "";

  LogService.info(
    `Dexie: checking for pending, ignored, and failed orders ${context}`
  );

  const statusesToCheck = [
    // Pending is the healthy transient state of a just-completed order
    // awaiting the drain; Ignored and Failed rows are stuck (validation
    // rejection / retries failing) and ship as WARNING so the log backend
    // can alert on them with the references included — this recurring line
    // is the authoritative "which orders are not persisted" signal,
    // including orders whose retry budget is exhausted.
    { status: SyncStatus.Pending, label: "pending", logLevel: LogLevel.Info },
    {
      status: SyncStatus.Ignored,
      label: "ignored",
      logLevel: LogLevel.Warning,
    },
    { status: SyncStatus.Failed, label: "failed", logLevel: LogLevel.Warning },
  ];

  for (const { status, label, logLevel } of statusesToCheck) {
    const result: string[] = [];

    await dexieDB.orders
      .where("syncStatus")
      .equals(status)
      .each((order) => {
        result.push(order.reference);
      });

    if (result.length > 0) {
      LogService.info(
        `Dexie: Warning - found ${
          result.length
        } ${label} orders, references: ${result.join(",")} ${context}`,
        { references: result },
        logLevel
      );
    }
  }

  const ordersCount = await dexieDB.orders.count();
  LogService.info(
    `Dexie: total number of queued orders: ${ordersCount} ${context}`
  );
}

/**
 * Clear Dexie db data
 */
export async function clearDexieDB() {
  LogService.info(
    "Dexie: clearing synced entities from Dexie db after closing sales"
  );
  try {
    // Closing sales is gated on the orders queue draining (synced rows are
    // deleted on success, retryable ones get up to MAX_SYNC_ATTEMPTS), so any
    // row remaining here is an ignored/exhausted one. Clear them all to avoid
    // accumulating across till sessions, but alert first with their ids so
    // they can be handled manually — each order's full payload is in the logs
    // (compressed), keyed by order id, from its enqueue in `saveOrderForSync`.
    const remainingOrders = await dexieDB.orders.toArray();
    if (remainingOrders.length > 0) {
      LogService.info(
        `ALERT: Clearing ${
          remainingOrders.length
        } unsynced order(s) from the sync queue on sales close, references: ${remainingOrders
          .map((order) => order.reference)
          .join(",")}`,
        {
          orders: remainingOrders.map((order) => ({
            id: order._id,
            reference: order.reference,
            syncStatus: order.syncStatus,
            attempts: order.attempts,
          })),
        },
        LogLevel.Error
      );
    }
    await dexieDB.orders.clear();
    // Delete synced transactions, unsynced ones are kept for retry
    await dexieDB.untypedTransactions
      .where("syncStatus")
      .equals(SyncStatus.Synced)
      .delete();
    LogService.info(
      `Dexie: cleared synced entities from Dexie db after closing sales`
    );
  } catch (error) {
    LogService.info(
      `Dexie: Error clearing Dexie db`,
      { params: error },
      LogLevel.Error
    );
  }
}
